import { styled } from '@mui/material/styles';
import { ButtonBase, Menu, MenuItem, Stack } from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { useState, type ReactNode } from 'react';

export interface DropdownFilterOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface Props<T extends string> {
  ariaLabel: string;
  labelPrefix: string;
  valueLabel: string;
  icon: ReactNode;
  options: DropdownFilterOption<T>[];
  onSelect: (value: T) => void;
}

function DropdownFilterInner<T extends string>({
  ariaLabel,
  labelPrefix,
  valueLabel,
  icon,
  options,
  onSelect,
}: Props<T>): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setAnchorEl(e.currentTarget as unknown as HTMLElement);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (value: T) => {
    onSelect(value);
    setAnchorEl(null);
  };

  return (
    <>
      <StyledTrigger
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
      >
        <TriggerContent>
          {icon}
          <FilterSortLabel>
            {labelPrefix} {valueLabel}
          </FilterSortLabel>
          <KeyboardArrowDown fontSize="small" color="action" aria-hidden />
        </TriggerContent>
      </StyledTrigger>
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {options.map(({ value, label }) => (
          <MenuItem key={value} onClick={() => handleSelect(value)}>
            {label}
          </MenuItem>
        ))}
      </StyledMenu>
    </>
  );
}

const StyledMenu = styled(Menu)(() => ({
  '& .MuiPaper-root': { minWidth: 240 },
}));

const FilterSortLabel = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: 500,
}));

const TriggerContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 2),
  borderRadius: theme.shape.borderRadius,
  width: '100%',
  minHeight: 40,
  fontSize: theme.typography.pxToRem(13),
  fontWeight: 500,
}));

const StyledTrigger = styled(ButtonBase)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mattersDropdown.trigger,
  '&:hover': {
    backgroundColor: theme.palette.mattersDropdown.triggerHover,
  },
}));

const DropdownFilter = DropdownFilterInner;

export default DropdownFilter;
